import React, { useMemo, useState } from "react";
import { Flag } from "lucide-react";
import { PALETTE, fmtDays, taxFreedomDay } from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const MONTHS_FR = [
  "Jan", "Fév", "Mars", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sept", "Oct", "Nov", "Déc",
];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * TaxCalendar — 365-day grid (12 columns × days), colored red (state) or green (self).
 * Bold marker on the Tax Freedom Day.
 */
export default function TaxCalendar({ ind }) {
  const daysForState = Math.max(
    0,
    Math.round(Number(ind?.jours_vie_sacrifies_par_an) || 0)
  );
  const year = new Date().getFullYear();
  const freedom = useMemo(() => taxFreedomDay(daysForState, year), [daysForState, year]);
  const freedomDayIndex = freedom ? freedom.day_number : null;

  const [hover, setHover] = useState(null); // {day, month, isState}

  // Build 12 column layout, 31 rows (empty squares for days that don't exist)
  const maxRows = 31;

  const isStateDay = (dayIndex /* 1-based day of year */) => dayIndex <= daysForState;

  // Compute day-of-year for a given month/day
  function dayOfYear(monthIdx /*0-based*/, dayNum /*1-based*/) {
    let d = 0;
    for (let i = 0; i < monthIdx; i++) d += DAYS_IN_MONTH[i];
    return d + dayNum;
  }

  const totalDays = 365;
  const selfDays = totalDays - daysForState;

  return (
    <Card data-testid="chart-calendar">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Calendrier de vie fiscale annuelle</span>
          <span className="text-xs font-normal text-muted-foreground">
            365 jours
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Legend */}
        <div className="mb-3 flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ backgroundColor: PALETTE.stateTax }}
            />
            <span>
              <strong>{daysForState}</strong> jours pour l'État
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ backgroundColor: PALETTE.freedom }}
            />
            <span>
              <strong>{selfDays}</strong> jours pour soi
            </span>
          </div>
          {freedom && daysForState > 0 && (
            <div
              className="ml-auto flex items-center gap-1.5 rounded-full border border-brand-gold/40 bg-accent/30 px-3 py-1"
              data-testid="tax-freedom-day-label"
            >
              <Flag className="h-3.5 w-3.5 text-brand-gold" />
              <span>
                Libération fiscale&nbsp;:{" "}
                <strong className="text-foreground">{freedom.label}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Grid: 12 columns (months) × 31 rows (days) */}
        <div
          className="grid w-full gap-0.5"
          style={{ gridTemplateColumns: "auto repeat(12, 1fr)" }}
          data-testid="calendar-grid"
          onMouseLeave={() => setHover(null)}
        >
          {/* Header row */}
          <div className="h-5" />
          {MONTHS_FR.map((m) => (
            <div
              key={m}
              className="flex h-5 items-center justify-center text-[10px] font-semibold text-muted-foreground"
            >
              {m}
            </div>
          ))}
          {/* Day rows */}
          {Array.from({ length: maxRows }, (_, r) => {
            const dayNum = r + 1;
            return (
              <React.Fragment key={r}>
                <div className="pr-1 text-right text-[9px] font-mono leading-[10px] text-muted-foreground">
                  {dayNum % 5 === 0 ? dayNum : ""}
                </div>
                {MONTHS_FR.map((_, m) => {
                  if (dayNum > DAYS_IN_MONTH[m]) {
                    return <div key={m} className="h-2.5 w-full" />;
                  }
                  const dayIdx = dayOfYear(m, dayNum);
                  const isState = isStateDay(dayIdx);
                  const isFreedom =
                    freedomDayIndex !== null && dayIdx === freedomDayIndex;
                  return (
                    <div
                      key={m}
                      onMouseEnter={() =>
                        setHover({ day: dayNum, month: MONTHS_FR[m], isState, dayIdx })
                      }
                      className="relative h-2.5 w-full cursor-default rounded-[1px] transition-opacity hover:opacity-80"
                      style={{
                        backgroundColor: isState
                          ? PALETTE.stateTax
                          : PALETTE.freedom,
                        outline: isFreedom ? `2px solid ${PALETTE.gold}` : "none",
                        outlineOffset: isFreedom ? 0 : 0,
                        zIndex: isFreedom ? 2 : 1,
                      }}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>

        {/* Tooltip */}
        <div
          className="mt-3 min-h-[32px] text-xs text-muted-foreground"
          data-testid="calendar-tooltip"
        >
          {hover ? (
            <>
              <span
                className="inline-block h-2 w-2 rounded-full mr-1.5"
                style={{
                  backgroundColor: hover.isState
                    ? PALETTE.stateTax
                    : PALETTE.freedom,
                }}
              />
              {hover.day} {hover.month} — jour {hover.dayIdx} / 365 —{" "}
              <strong>
                {hover.isState ? "Travaillé pour l'État" : "Travaillé pour soi"}
              </strong>
            </>
          ) : (
            <>Passez le curseur sur un jour pour le détail.</>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
          <span className="text-muted-foreground">
            Jours de vie sacrifiés / an
          </span>
          <span className="font-mono font-bold">{fmtDays(daysForState)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
